import { useState, useEffect, useContext, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { UserContext } from "../context/UserContext";
import { useSessionValidation } from "../hooks/useSessionValidation";
import SearchField from "./SearchField";
import HeaderTable from "./HeaderTable";
import ScanFields from "./ScanFields";
import LinesTable from "./LinesTable";
import ActionButtons from "./ActionButtons";
import { Header, Line, ScanFields as ScanFieldsType } from "../types";
import { useSearchHeaders } from "../hooks/useSearchHeaders";
import { useLineDetails } from "../hooks/useLineDetails";
import { useSubinventoryLov } from "../hooks/useSubinventoryLov";
import { useLineManager } from "./LineManager";
import { useSerialNumberScanner } from "./SerialNumberScanner";
import { getCommonStyles } from "../styles/commonStyles";

const Dashboard = () => {
  const { userId, responsibilityId, sessionId } = useContext(UserContext);
  const { validateSession } = useSessionValidation();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const sessionValidated = useRef(false);
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedHeader, setSelectedHeader] = useState<Header | undefined>();
  const [scanFields, setScanFields] = useState<ScanFieldsType>({
    scanSerialNumber: "",
    toSubinventory: "",
    toLocator: "",
  });
  const [isDarkTheme, setIsDarkTheme] = useState(false);
  const [updatedLines, setUpdatedLines] = useState<Line[]>([]);

  const { headers: filteredHeaders, error, searchHeaders } = useSearchHeaders();
  const { lines: headerLines, error: linesError } = useLineDetails(
    selectedHeader?.requestNumber
  );

  const currentLines = selectedHeader
    ? updatedLines.length > 0
      ? updatedLines
      : headerLines
    : [];

  const lineManager = useLineManager({
    lines: currentLines,
    onLinesUpdate: setUpdatedLines,
    selectedHeaderToOrg: selectedHeader?.toOrg,
  });

  const serialScanner = useSerialNumberScanner({
    lines: currentLines,
    scanFields,
    onLinesUpdate: setUpdatedLines,
    onScanFieldsUpdate: setScanFields,
  });

  useEffect(() => {
    if (sessionId === "" || sessionId === undefined || sessionId === null) {
      navigate("/unauthorized");
    } else {
      if (!sessionValidated.current) {
        sessionValidated.current = true;
        validateSession(
          {
            user_id: parseInt(userId),
            resp_id: parseInt(responsibilityId),
            session_id: parseInt(sessionId),
          },
          (response: any) => {
            if (
              response &&
              response?.userId === Number(userId) &&
              response?.sessionId === Number(sessionId)
            ) {
              setIsLoggedIn(true);
            } else {
              navigate("/unauthorized");
            }
          }
        );
      }
    }
  }, [
    sessionId,
    navigate,
    responsibilityId,
    userId,
  ]);

  useEffect(() => {
    document.body.style.backgroundColor = isDarkTheme ? "#1a1a1a" : "#ffffff";
    document.body.style.margin = "0";
    document.body.style.padding = "0";
  }, [isDarkTheme]);

  const handleSearchTermChange = (newSearchTerm: string) => {
    setSearchTerm(newSearchTerm);
    if (!newSearchTerm.trim()) {
      searchHeaders("");
      setSelectedHeader(undefined);
      setScanFields({
        scanSerialNumber: "",
        toSubinventory: "",
        toLocator: "",
      });
      setUpdatedLines([]);
    }
  };

  const handleSearch = () => {
    searchHeaders(searchTerm);
    setSelectedHeader(undefined);
    setScanFields({ scanSerialNumber: "", toSubinventory: "", toLocator: "" });
    setUpdatedLines([]);
  };

  const { data: subinventories, refetch: refetchSubinventories } =
    useSubinventoryLov(selectedHeader?.toOrg, false);

  const handleHeaderClick = (header: Header) => {
    setSelectedHeader(header);
    setScanFields({ scanSerialNumber: "", toSubinventory: "", toLocator: "" });
    setUpdatedLines([]);
    lineManager.clearNewRowIds();

    if (header.toOrg) {
      refetchSubinventories(header.toOrg);
    }
  };

  const handleCancel = () => {
    lineManager.cancelChanges();
    setScanFields({ scanSerialNumber: "", toSubinventory: "", toLocator: "" });
  };

  if (!isLoggedIn) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        Validating session...
      </div>
    );
  }

  const themeStyles = {
    backgroundColor: isDarkTheme ? "#1a1a1a" : "#ffffff",
    color: isDarkTheme ? "#ffffff" : "#000000",
    minHeight: "100vh",
  };

  const commonStyles = getCommonStyles(isDarkTheme);

  return (
    <div
      style={{
        ...themeStyles,
        padding: "20px",
        fontFamily: "Arial, sans-serif",
        height: "100vh",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "0px",
        }}
      >
        <h1>RMA Receive & Equipment Return Dashboard</h1>
        <div
          onClick={() => setIsDarkTheme(!isDarkTheme)}
          style={{
            width: "50px",
            height: "25px",
            backgroundColor: isDarkTheme ? "#007bff" : "#ccc",
            borderRadius: "25px",
            position: "relative",
            cursor: "pointer",
            transition: "background-color 0.3s",
            display: "flex",
            alignItems: "center",
            justifyContent: isDarkTheme ? "flex-start" : "flex-end",
            padding: "2px",
          }}
        >
          <div
            style={{
              width: "21px",
              height: "21px",
              backgroundColor: "white",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "12px",
              transition: "all 0.3s",
            }}
          >
            {isDarkTheme ? "☾" : "☀"}
          </div>
        </div>
      </div>

      <hr style={commonStyles.separator} />

      <SearchField
        value={searchTerm}
        onChange={handleSearchTermChange}
        onSearch={handleSearch}
        isDarkTheme={isDarkTheme}
      />

      {error && (
        <div style={{ padding: "20px", color: "red" }}>Error: {error}</div>
      )}
      <HeaderTable
        headers={filteredHeaders}
        onHeaderClick={handleHeaderClick}
        selectedHeader={selectedHeader}
        isDarkTheme={isDarkTheme}
      />

      <ScanFields
        scanFields={scanFields}
        onScanFieldsChange={setScanFields}
        onSerialNumberScan={serialScanner.scanSerialNumber}
        isDarkTheme={isDarkTheme}
        selectedHeaderToOrg={selectedHeader?.toOrg}
        lines={currentLines}
        onAddRow={lineManager.addNewRow}
        preLoadedSubinventories={subinventories}
      />

      {linesError && (
        <div style={{ padding: "20px", color: "red" }}>
          Lines Error: {linesError}
        </div>
      )}

      <div style={{ marginBottom: "5px" }}>
        <LinesTable
          lines={currentLines}
          isDarkTheme={isDarkTheme}
          onLineUpdate={lineManager.updateLine}
          selectedHeaderToOrg={selectedHeader?.toOrg}
          newRowIds={lineManager.newRowIds}
        />
      </div>

      <ActionButtons
        isDarkTheme={isDarkTheme}
        onCancel={handleCancel}
        onSave={() => alert("Save functionality to be implemented")}
        onSubmit={() => alert("Submit functionality to be implemented")}
      />
    </div>
  );
};

export default Dashboard;
